export type AspectRatioKey = 'square' | 'portrait' | 'landscape' | 'classic'
export type GridPresetKey = 'auto' | 'columns' | 'hero-left' | 'hero-top'
export type OutputFormat = 'image/jpeg' | 'image/png'

import {
  coverSourceRect as sharedCoverSourceRect,
  type FocalPoint,
  type SourceRect,
} from '@/shared/image/cropGeometry'

export type { FocalPoint, SourceRect } from '@/shared/image/cropGeometry'

export interface ImageMeta {
  width: number
  height: number
  bytes: number
}

export interface LayoutCell {
  x: number
  y: number
  width: number
  height: number
}

export interface ExportSize {
  width: number
  height: number
}

export type GuardrailCode =
  | 'image_count'
  | 'file_too_large'
  | 'total_bytes'
  | 'image_dimensions'
  | 'source_pixels'
  | 'output_pixels'
  | 'memory'

export class CollageGuardrailError extends Error {
  constructor(readonly code: GuardrailCode) {
    super(code)
    this.name = 'CollageGuardrailError'
  }
}

export const MIN_IMAGES = 2
export const MAX_IMAGES = 12
export const MAX_FILE_BYTES = 50 * 1024 * 1024
export const MAX_TOTAL_BYTES = 150 * 1024 * 1024
export const MAX_IMAGE_PIXELS = 36_000_000
export const MAX_SOURCE_PIXELS = 72_000_000
export const MAX_OUTPUT_PIXELS = 16_000_000
export const MAX_ESTIMATED_MEMORY_BYTES = 384 * 1024 * 1024
export const EXPORT_TIME_BUDGET_MS = 12_000

export const ASPECT_SIZES: Record<AspectRatioKey, ExportSize> = {
  square: { width: 1600, height: 1600 },
  portrait: { width: 1600, height: 2000 },
  landscape: { width: 1920, height: 1080 },
  classic: { width: 1800, height: 1200 },
}

function regularGrid(count: number, columns: number): LayoutCell[] {
  const rows = Math.ceil(count / columns)
  return Array.from({ length: count }, (_, index) => {
    const row = Math.floor(index / columns)
    const column = index % columns
    const itemsInRow = row === rows - 1 ? count - row * columns : columns
    const rowOffset = (columns - itemsInRow) / (2 * columns)
    return {
      x: rowOffset + column / columns,
      y: row / rows,
      width: 1 / columns,
      height: 1 / rows,
    }
  })
}

export function buildLayout(count: number, preset: GridPresetKey): LayoutCell[] {
  if (!Number.isInteger(count) || count < MIN_IMAGES || count > MAX_IMAGES) {
    throw new CollageGuardrailError('image_count')
  }

  if (preset === 'hero-left') {
    const rest = regularGrid(count - 1, Math.min(2, count - 1))
    return [
      { x: 0, y: 0, width: 0.5, height: 1 },
      ...rest.map((cell) => ({
        x: 0.5 + cell.x * 0.5,
        y: cell.y,
        width: cell.width * 0.5,
        height: cell.height,
      })),
    ]
  }

  if (preset === 'hero-top') {
    const rest = regularGrid(count - 1, Math.min(4, count - 1))
    return [
      { x: 0, y: 0, width: 1, height: 0.5 },
      ...rest.map((cell) => ({
        x: cell.x,
        y: 0.5 + cell.y * 0.5,
        width: cell.width,
        height: cell.height * 0.5,
      })),
    ]
  }

  const columns = preset === 'columns'
    ? Math.min(2, count)
    : Math.ceil(Math.sqrt(count))
  return regularGrid(count, columns)
}

export function coverSourceRect(
  sourceWidth: number,
  sourceHeight: number,
  destinationWidth: number,
  destinationHeight: number,
  focal: FocalPoint,
): SourceRect {
  try {
    return sharedCoverSourceRect(
      sourceWidth,
      sourceHeight,
      destinationWidth,
      destinationHeight,
      focal,
    )
  } catch {
    throw new CollageGuardrailError('image_dimensions')
  }
}

export function validateImageMeta(items: readonly ImageMeta[]): void {
  if (items.length < MIN_IMAGES || items.length > MAX_IMAGES) {
    throw new CollageGuardrailError('image_count')
  }

  let totalBytes = 0
  let totalPixels = 0
  for (const item of items) {
    if (item.bytes > MAX_FILE_BYTES) {
      throw new CollageGuardrailError('file_too_large')
    }
    if (
      !Number.isSafeInteger(item.width) ||
      !Number.isSafeInteger(item.height) ||
      item.width <= 0 ||
      item.height <= 0
    ) {
      throw new CollageGuardrailError('image_dimensions')
    }
    const pixels = item.width * item.height
    if (!Number.isSafeInteger(pixels) || pixels > MAX_IMAGE_PIXELS) {
      throw new CollageGuardrailError('image_dimensions')
    }
    totalBytes += item.bytes
    totalPixels += pixels
  }

  if (totalBytes > MAX_TOTAL_BYTES) {
    throw new CollageGuardrailError('total_bytes')
  }
  if (totalPixels > MAX_SOURCE_PIXELS) {
    throw new CollageGuardrailError('source_pixels')
  }
}

export function validateExportResources(
  items: readonly ImageMeta[],
  size: ExportSize,
): void {
  validateImageMeta(items)
  const outputPixels = size.width * size.height
  if (
    !Number.isSafeInteger(outputPixels) ||
    size.width <= 0 ||
    size.height <= 0 ||
    outputPixels > MAX_OUTPUT_PIXELS
  ) {
    throw new CollageGuardrailError('output_pixels')
  }

  const sourcePixels = items.reduce((sum, item) => sum + item.width * item.height, 0)
  const estimatedMemory = (sourcePixels + outputPixels * 2) * 4
  if (!Number.isSafeInteger(estimatedMemory) || estimatedMemory > MAX_ESTIMATED_MEMORY_BYTES) {
    throw new CollageGuardrailError('memory')
  }
}

export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return [...items]
  }
  const next = [...items]
  const item = next.splice(from, 1)[0] as T
  next.splice(to, 0, item)
  return next
}
